import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  setTargetWord,
  handleKeyPress as reduxHandleKeyPress,
  submitGuess,
  resetGame,
} from '../../../store/slices/learndleWordSlice';
import Keyboard from './Keyboard';
import { useGetNextLevel } from '../../../hooks/useGetNextLevel';
import { updateLevelRef } from '../../../store/slices/levelSlice';

const LearndelGame = () => {
  const { level } = useGetNextLevel('learndle-word');
  const dispatch = useDispatch();
  const gameContentRef = useRef(null);
  const [isSubmittingGuess, setIsSubmittingGuess] = useState(false);

  // Select state from Redux store
  const {
    targetWord,
    currentGuess,
    guesses,
    invalidWord,
    hasWon,
    activeTileIndex,
    gameStats,
    error,
  } = useSelector((state) => state.learndleWord || {});

  const handleNextWord = () => {
    dispatch(resetGame());
    dispatch(updateLevelRef());
  };

  // Initialize the game with the provided word
  useEffect(() => {
    if (level && level.word && level.word !== targetWord) {
      dispatch(setTargetWord(level.word));
    }
  }, [level, targetWord, dispatch]);

  // Function to scroll to bottom of game content
  const scrollToBottom = () => {
    if (gameContentRef.current) {
      gameContentRef.current.scrollTop = gameContentRef.current.scrollHeight;
    }
  };

  // Handler for on-screen keyboard presses
  const handleKeyPress = (key) => {
    if (key === 'ENTER') {
      setIsSubmittingGuess(true);
      dispatch(reduxHandleKeyPress(key));
      // Dispatch the async thunk to submit the guess
      dispatch(submitGuess());
      // Scroll after submitting guess with longer delay and reset flag
      setTimeout(() => {
        scrollToBottom();
        setIsSubmittingGuess(false);
      }, 200);
    } else if (!isSubmittingGuess) {
      // Only handle other keys if not currently submitting
      dispatch(reduxHandleKeyPress(key));
      // Scroll immediately for regular letter presses
      setTimeout(scrollToBottom, 50);
    }
  };

  const checkGuess = (guess) => {
    if (!targetWord) return [];

    const result = [];
    const targetLetters = [...targetWord];

    for (let i = 0; i < guess.length; i++) {
      if (guess[i].toUpperCase() === targetWord[i].toUpperCase()) {
        result.push('correct');
      } else if (targetLetters.includes(guess[i].toUpperCase())) {
        result.push('present');
      } else {
        result.push('absent');
      }
    }
    return result;
  };

  const renderCurrentRow = () => {
    if (hasWon || !currentGuess) return null;

    return (
      <div className="guess-input-container">
        <div className="guess current">
          {currentGuess.map((letter, i) => (
            <div
              key={i}
              className={`tile-div ${i === activeTileIndex ? 'active' : ''}`}
            >
              {letter}
            </div>
          ))}
        </div>
        {invalidWord && (
          <div className="error-message">Not a valid English word</div>
        )}
        {error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  if (!targetWord) {
    return <div>Loading game...</div>;
  }

  return (
    <div className="child-mode learndle-game">
      <div className="game-content" ref={gameContentRef}>
        <div className="guesses">
          {guesses &&
            guesses.map((guess, index) => (
              <div key={index} className="guess">
                {[...guess].map((letter, i) => (
                  <span
                    key={i}
                    className={letter === '_' ? 'empty' : checkGuess(guess)[i]}
                  >
                    {letter === '_' ? '' : letter}
                  </span>
                ))}
              </div>
            ))}
          {renderCurrentRow()}
          {hasWon && (
            <div className="win-message">
              <h2>Well done! 🎉</h2>
              <p>You found the word: {targetWord}</p>
              <p>You completed it in {gameStats?.guessCount} guesses!</p>
              <button
                onClick={() => handleNextWord()}
                className="next-word-button"
              >
                Play Again
              </button>
            </div>
          )}
        </div>
      </div>

      {!hasWon && (
        <Keyboard
          onKeyPress={handleKeyPress}
          gameState={{ targetWord, guesses: guesses || [] }}
        />
      )}
    </div>
  );
};

LearndelGame.propTypes = {
  word: PropTypes.string,
};

export default LearndelGame;
